import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { 
    LayoutDashboard, Home, FileText, CreditCard, 
    Wrench, BarChart3, Settings, Users, Car, Building2,
    Menu, X, Brain, MessageSquare, Shield, PieChart,
    AlertCircle, Upload

} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

const Sidebar = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const { user } = useAuth();

    const getMenuItems = () => {
        const role = user?.role;

        if (role === 'superadmin') {
            return [
                { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
                { path: '/assets', icon: Home, label: 'Assets' },
                { path: '/leases', icon: FileText, label: 'Leases' },
                { path: '/payments', icon: CreditCard, label: 'Payments', exact: true },
                { path: '/payments/overview', icon: PieChart, label: 'Asset Overview' },
                { path: '/payments/unattributed', icon: AlertCircle, label: 'Unattributed' },
                { path: '/maintenance', icon: Wrench, label: 'Maintenance' },
                { path: '/reports', icon: BarChart3, label: 'Reports' },
                { path: '/tenants', icon: Users, label: 'Tenants' },
                { path: '/chat', icon: MessageSquare, label: 'Chat' },
                { path: '/ai', icon: Brain, label: 'AI Insights' },
                { path: '/sms', icon: MessageSquare, label: 'SMS' },
                { path: '/bulk-import', icon: Upload, label: 'Bulk Import' },
                { path: '/settings', icon: Settings, label: 'Settings' },
            ];
        }

        if (role === 'landlord') {
            return [
                { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
                { path: '/assets', icon: Home, label: 'Assets' },
                { path: '/leases', icon: FileText, label: 'Leases' },
                { path: '/payments', icon: CreditCard, label: 'Payments', exact: true },
                { path: '/payments/overview', icon: PieChart, label: 'Asset Overview' },
                { path: '/payments/unattributed', icon: AlertCircle, label: 'Unattributed' },
                { path: '/maintenance', icon: Wrench, label: 'Maintenance' },
                { path: '/reports', icon: BarChart3, label: 'Reports' },
                { path: '/tenants', icon: Users, label: 'Tenants' },
                { path: '/chat', icon: MessageSquare, label: 'Chat' },
                { path: '/ai', icon: Brain, label: 'AI Insights' },
                { path: '/sms', icon: MessageSquare, label: 'SMS' },
                { path: '/bulk-import', icon: Upload, label: 'Bulk Import' },
                { path: '/settings', icon: Settings, label: 'Settings' },
            ];
        }

        if (role === 'caretaker') {
            return [
                { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
                { path: '/assets', icon: Home, label: 'Assets' },
                { path: '/leases', icon: FileText, label: 'Leases' },
                { path: '/payments', icon: CreditCard, label: 'Payments', exact: true },
                { path: '/payments/overview', icon: PieChart, label: 'Asset Overview' },
                { path: '/maintenance', icon: Wrench, label: 'Maintenance' },
                { path: '/reports', icon: BarChart3, label: 'Reports' },
                { path: '/chat', icon: MessageSquare, label: 'Chat' },
            ];
        }

        if (role === 'tenant') {
            return [
                { path: '/portal', icon: LayoutDashboard, label: 'My Portal' },
            ];
        }

        return [];
    };

    const menuItems = getMenuItems();

    const adminMenuItems = [
        { path: '/superadmin', icon: Shield, label: 'Super Admin' },
    ];

    const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
    const closeMobileMenu = () => setIsMobileMenuOpen(false);

    const SidebarContent = () => (
        <>
            <div className="p-4 border-b border-gray-800">
                <div className="flex items-center space-x-2">
                    <Building2 className="h-8 w-8 text-indigo-400" />
                    <span className="font-bold text-xl text-white">Brominon</span>
                </div>
            </div>
            
            <nav className="flex-1 mt-6">
                {menuItems.map((item) => (
                    <NavLink
                        key={item.path}
                        to={item.path}
                        end={item.exact || false}
                        onClick={closeMobileMenu}
                        className={({ isActive }) =>
                            `flex items-center px-4 py-3 text-gray-300 hover:bg-gray-800 transition-colors ${
                                isActive ? 'bg-gray-800 text-white border-r-4 border-indigo-500' : ''
                            }`
                        }
                    >
                        <item.icon className="h-5 w-5 mr-3" />
                        <span>{item.label}</span>
                    </NavLink>
                ))}

                {user?.role === 'superadmin' && (
                    <>
                        <div className="border-t border-gray-800 my-2"></div>
                        <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Administration
                        </div>
                        {adminMenuItems.map((item) => (
                            <NavLink
                                key={item.path}
                                to={item.path}
                                onClick={closeMobileMenu}
                                className={({ isActive }) =>
                                    `flex items-center px-4 py-3 text-gray-300 hover:bg-gray-800 transition-colors ${
                                        isActive ? 'bg-gray-800 text-white border-r-4 border-indigo-500' : ''
                                    }`
                                }
                            >
                                <item.icon className="h-5 w-5 mr-3" />
                                <span>{item.label}</span>
                            </NavLink>
                        ))}
                    </>
                )}
            </nav>
        </>
    );

    return (
        <>
            <button
                onClick={toggleMobileMenu}
                className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-md bg-gray-900 text-white hover:bg-gray-800 transition-colors"
                aria-label="Toggle menu"
            >
                {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>

            <div
                className={`fixed inset-0 z-40 lg:hidden transition-transform duration-300 ease-in-out ${
                    isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
                }`}
            >
                <div className="absolute inset-0 bg-black bg-opacity-50" onClick={closeMobileMenu} />
                <aside className="relative w-64 bg-gray-900 text-white flex flex-col h-full overflow-y-auto">
                    <SidebarContent />
                </aside>
            </div>

            <aside className="hidden lg:flex w-64 bg-gray-900 text-white flex-col h-screen sticky top-0 overflow-y-auto">
                <SidebarContent />
            </aside>
        </>
    );
};

export default Sidebar;
